import React, { useState, useEffect } from 'react';
import BackWithSidebar from '../../../../components/BackWithSidebar';
import GeneralIcon from '../../../../assets';
import SearchBar from '../../../../components/SearchBar';
import discountService from '../../../../services/discountService';
import AdicionarDesconto from './Descontos/AdicionarDescontoModal';
import DetalhesDesconto from './Descontos/DetalhesDesconto';

function EmptyState() {
  return (
    <div className="text-center py-16 text-gray-400">
      <GeneralIcon iconName="vazio" className="mx-auto mb-4 h-40 w-40" />
      <div className="text-sm">Nenhum desconto encontrado</div>
    </div>
  );
}

export default function Descontos() {
  const [discounts, setDiscounts] = useState([]);
  const [search, setSearch] = useState('');
  const [activeFilters, setActiveFilters] = useState({
    type: [],
    status: [],
  });
  const [filtersMenuOpen, setFiltersMenuOpen] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [discountToEdit, setDiscountToEdit] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [selectedDiscount, setSelectedDiscount] = useState(null);

  // Load discounts and auto-finalize expired
  useEffect(() => {
    async function init() {
      await discountService.autoFinalizeExpired();
      await loadDiscounts();
    }
    init();
  }, []);

  async function loadDiscounts() {
    try {
      const data = await discountService.fetchDiscounts();
      setDiscounts(data || []);
    } catch (err) {
      console.error('Error loading discounts:', err);
      setDiscounts([]);
    }
  }

  // Filter discounts
  const filteredDiscounts = discounts.filter(d => {
    // Search filter
    if (search.trim()) {
      const query = search.toLowerCase();
      const matchName = d.name?.toLowerCase().includes(query);
      const matchCode = d.code?.toLowerCase().includes(query);
      if (!matchName && !matchCode) return false;
    }

    // Type filter
    if (activeFilters.type.length > 0) {
      if (!activeFilters.type.includes(d.type)) return false;
    }

    // Status filter (Ativo, Desativado, Finalizado)
    if (activeFilters.status.length > 0) {
      const statusMatch = activeFilters.status.some(s => {
        if (s === 'Ativo') return d.active && !d.finished;
        if (s === 'Desativado') return !d.active && !d.finished;
        if (s === 'Finalizado') return d.finished;
        return false;
      });
      if (!statusMatch) return false;
    }

    return true;
  });

  function toggleFilterOption(key, value) {
    setActiveFilters(prev => {
      const current = prev[key] || [];
      const exists = current.includes(value);
      const next = exists ? current.filter(x => x !== value) : [...current, value];
      return { ...prev, [key]: next };
    });
  }

  function removeFilterChip(key, value) {
    setActiveFilters(prev => {
      const current = prev[key] || [];
      const next = current.filter(x => x !== value);
      return { ...prev, [key]: next };
    });
  }

  function handleAddNew() {
    setDiscountToEdit(null);
    setShowAdd(true);
  }

  function handleEdit(discount) {
    setDiscountToEdit(discount);
    setShowAdd(true);
  }

  function handleViewDetails(discount) {
    setSelectedDiscount(discount);
    setShowDetails(true);
  }

  async function handleDelete(id) {
    if (!window.confirm('Tem certeza que deseja excluir este desconto?')) return;

    try {
      await discountService.deleteDiscount(id);
      await loadDiscounts();
    } catch (err) {
      console.error('Error deleting discount:', err);
    }
  }

  async function handleToggleStatus(id) {
    try {
      await discountService.toggleDiscountStatus(id);
      await loadDiscounts();
    } catch (err) {
      console.error('Error toggling status:', err);
    }
  }

  function formatDate(dateString) {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR');
  }

  return (
    <div className="p-4 max-w-3xl mx-auto">
      <div className="flex items-center gap-3 mb-4">
        <BackWithSidebar to="/dashboard" className="p-2 rounded-full hover:bg-gray-100" />
        <h2 className="text-lg font-semibold">Descontos</h2>
      </div>

      <div className="bg-white rounded-lg p-4 shadow-sm">
        {/* Search and Filter */}
        <div className="flex gap-3 items-center mb-4">
          <SearchBar
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Pesquisar por desconto"
            className="flex-1"
          />
          <div className="relative">
            <button
              type="button"
              onClick={() => setFiltersMenuOpen(s => !s)}
              className="border rounded px-3 py-2 bg-gray-50 hover:bg-gray-100 transition-colors"
            >
              Filtrar
            </button>
            {filtersMenuOpen && (
              <div className="absolute right-0 mt-2 w-64 bg-white border rounded shadow-lg p-3 z-20">
                {/* Type filters */}
                <div className="mb-3">
                  <div className="text-xs font-medium text-gray-600 mb-2">Tipo</div>
                  <div className="flex flex-col gap-1">
                    {['Cupom', 'Promoção', 'Ativo'].map(type => (
                      <label key={type} className="text-sm flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded">
                        <input
                          type="checkbox"
                          checked={(activeFilters.type || []).includes(type)}
                          onChange={() => toggleFilterOption('type', type)}
                          className="rounded border-gray-300"
                        />
                        <span>{type}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Status filters */}
                <div className="mb-3">
                  <div className="text-xs font-medium text-gray-600 mb-2">Status</div>
                  <div className="flex flex-col gap-1">
                    {['Ativo', 'Desativado', 'Finalizado'].map(status => (
                      <label key={status} className="text-sm flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded">
                        <input
                          type="checkbox"
                          checked={(activeFilters.status || []).includes(status)}
                          onChange={() => toggleFilterOption('status', status)}
                          className="rounded border-gray-300"
                        />
                        <span>{status}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="text-right pt-2 border-t">
                  <button
                    type="button"
                    onClick={() => setFiltersMenuOpen(false)}
                    className="text-sm text-gray-600 hover:text-gray-800"
                  >
                    Fechar
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Filter chips */}
        <div className="flex gap-2 flex-wrap mb-3">
          {Object.entries(activeFilters).flatMap(([k, arr]) =>
            arr.map(v => ({ k, v }))
          ).map(({ k, v }) => (
            <div
              key={k + ':' + v}
              className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-sm flex items-center gap-2"
            >
              <span>{v}</span>
              <button onClick={() => removeFilterChip(k, v)} className="text-purple-700 hover:text-purple-900">
                ×
              </button>
            </div>
          ))}
        </div>

        {/* Results count */}
        <div className="text-sm text-gray-600 mb-4">
          Encontramos {filteredDiscounts.length} resultado{filteredDiscounts.length !== 1 ? 's' : ''}
        </div>

        {/* Discount list */}
        {filteredDiscounts.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="space-y-3">
            {filteredDiscounts.map(discount => {
              const isFinished = discount.finished;
              const isActive = discount.active && !isFinished;

              return (
                <div
                  key={discount.id}
                  onClick={() => handleViewDetails(discount)}
                  className="border rounded-lg p-4 cursor-pointer hover:shadow-md transition-shadow bg-white"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="font-semibold text-gray-900">{discount.name}</div>
                      {discount.description && (
                        <div className="text-sm text-gray-600 mt-1">{discount.description}</div>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleStatus(discount.id);
                        }}
                        disabled={isFinished}
                        className={`p-2 rounded-lg transition-colors ${
                          isFinished
                            ? 'opacity-50 cursor-not-allowed'
                            : isActive
                            ? 'bg-green-50 text-green-600 hover:bg-green-100'
                            : 'bg-red-50 text-red-600 hover:bg-red-100'
                        }`}
                        title={isFinished ? 'Finalizado' : isActive ? 'Desativar' : 'Ativar'}
                      >
                        <GeneralIcon
                          iconName={isActive ? 'ligar' : 'desligar'}
                          className="w-5 h-5"
                        />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(discount);
                        }}
                        className="p-2 rounded-lg text-purple-600 hover:bg-purple-50 transition-colors"
                      >
                        <GeneralIcon iconName="editar" className="w-5 h-5" />
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <div className="text-xs text-gray-500">Tipo</div>
                      <div className="text-gray-900">{discount.type}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-gray-500">Valor</div>
                      <div className="text-gray-900 font-semibold">{discount.value}%</div>
                    </div>
                  </div>

                  {discount.code && (
                    <div className="mt-2 text-sm">
                      <div className="text-xs text-gray-500">Código</div>
                      <div className="text-gray-900 font-mono">{discount.code}</div>
                    </div>
                  )}

                  {discount.finalDate && (
                    <div className="mt-2 text-sm">
                      <div className="text-xs text-gray-500">Data de finalização</div>
                      <div className={`${
                        isFinished ? 'text-red-600 font-medium' : 'text-gray-900'
                      }`}>
                        {formatDate(discount.finalDate)}
                      </div>
                    </div>
                  )}

                  {isFinished && (
                    <div className="mt-3 inline-flex items-center gap-1.5 px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded">
                      <span className="w-1.5 h-1.5 bg-blue-500 rounded-full"></span>
                      Finalizado
                    </div>
                  )}
                  {!isActive && !isFinished && (
                    <div className="mt-3 inline-flex items-center gap-1.5 px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full"></span>
                      Desativado
                    </div>
                  )}
                  {isActive && (
                    <div className="mt-3 inline-flex items-center gap-1.5 px-2 py-1 bg-green-50 text-green-700 text-xs rounded">
                      <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
                      Ativo
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Add button */}
        <div className="mt-6">
          <button
            onClick={handleAddNew}
            className="w-full py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center gap-2"
          >
            <span className="text-xl">+</span>
            Adicionar um novo desconto
          </button>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showAdd && (
        <AdicionarDesconto
          discount={discountToEdit}
          onClose={() => setShowAdd(false)}
          onSave={loadDiscounts}
        />
      )}

      {/* Details Modal */}
      {showDetails && selectedDiscount && (
        <DetalhesDesconto
          discount={selectedDiscount}
          onClose={() => {
            setShowDetails(false);
            setSelectedDiscount(null);
          }}
          onSave={loadDiscounts}
        />
      )}
    </div>
  );
}
